import { useState } from 'react';
import { Alert, Button, Group, Modal, Select, Stack, Text, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MISS_REASON_LABELS, MISS_REASONS, type MissReason } from '@prooffleet/contracts';
import { api } from '@prooffleet/client';
import { EmptyState, PageHeader } from '../components/Page.js';

interface MissRow {
  _id: string;
  unitNumber: string;
  templateKey: string;
  dueAt: string;
  windowEndsAt: string;
  reason: string | null;
  note: string | null;
}

/**
 * Журнал пропусков.
 *
 * Правило 9: отсутствие осмотра — тоже запись. Объяснённый пропуск безопасен,
 * молчаливый превращается в улику против компании, поэтому необъяснённые
 * вынесены наверх и подсвечены.
 */
function isMissReason(value: string | null): value is MissReason {
  return value !== null && (MISS_REASONS as readonly string[]).includes(value);
}

export function Missed(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<MissRow | null>(null);
  const [reason, setReason] = useState<MissReason | null>(null);
  const [note, setNote] = useState('');

  const { data } = useQuery({
    queryKey: ['missed'],
    queryFn: () => api<{ missed: MissRow[] }>('/missed-inspections'),
  });

  const explain = useMutation({
    mutationFn: async (input: { id: string; reason: MissReason; note: string | null }) =>
      api(`/missed-inspections/${input.id}/explain`, {
        method: 'POST',
        body: { reason: input.reason, note: input.note },
      }),
    onSuccess: async () => {
      setTarget(null);
      setReason(null);
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['missed'] });
    },
  });

  const rows = data?.missed ?? [];
  const unexplained = rows.filter((r) => r.reason === null);

  return (
    <Stack gap={0}>
      <PageHeader
        title="Missed inspections"
        count={rows.length}
        hint="A missing inspection is itself a record. An explained miss is safe; a silent gap works against the fleet."
      />

      {unexplained.length > 0 && (
        <Alert color="orange" mb="md">
          {unexplained.length === 1
            ? '1 miss has no reason on record.'
            : `${String(unexplained.length)} misses have no reason on record.`}
        </Alert>
      )}

      {rows.length === 0 && (
        <EmptyState
          tone="good"
          title="No missed inspections"
          hint="Every scheduled inspection in this period was submitted on time."
        />
      )}

      <Stack gap={0}>
        {rows.map((row) => (
          <div
            key={row._id}
            className={`pf-stripe pf-stripe-${row.reason === null ? 'warn' : 'ok'}`}
            style={{ borderBottom: '1px solid var(--pf-line)', padding: '14px 16px' }}
          >
            <Group justify="space-between" wrap="nowrap" align="flex-start" gap="lg">
              <div style={{ minWidth: 0 }}>
                <Group gap={8} align="baseline">
                  <Text fw={660} fz="sm">
                    {row.unitNumber}
                  </Text>
                  <Text className="pf-mono">{row.templateKey}</Text>
                </Group>
                <Text fz="sm" c="gray.7" mt={3}>
                  Due {new Date(row.dueAt).toLocaleString()} · window closed{' '}
                  {new Date(row.windowEndsAt).toLocaleTimeString()}
                </Text>
                {row.note !== null && (
                  <Text fz="sm" c="dimmed" mt={4}>
                    {row.note}
                  </Text>
                )}
              </div>

              {row.reason === null ? (
                <Button
                  size="xs"
                  onClick={() => {
                    setTarget(row);
                    setReason(null);
                    setNote('');
                  }}
                >
                  Add reason
                </Button>
              ) : (
                <Text fz="xs" c="dimmed" style={{ flex: '0 0 auto' }}>
                  {MISS_REASON_LABELS[row.reason as MissReason] ?? row.reason}
                </Text>
              )}
            </Group>
          </div>
        ))}
      </Stack>

      <Modal opened={target !== null} onClose={() => setTarget(null)} title="Reason for the miss">
        <Stack gap="md">
          <Text fz="sm" c="dimmed">
            The reason is written once: it is one person's explanation at one moment, not an
            editable form field.
          </Text>

          <Select
            label="Reason"
            data={MISS_REASONS.map((value) => ({ value, label: MISS_REASON_LABELS[value] }))}
            value={reason}
            onChange={(value) => setReason(isMissReason(value) ? value : null)}
          />
          <Textarea
            label="Comment"
            autosize
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />

          <Button
            disabled={reason === null}
            loading={explain.isPending}
            onClick={() => {
              if (target !== null && reason !== null) {
                explain.mutate({ id: target._id, reason, note: note === '' ? null : note });
              }
            }}
          >
            Save
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
